import React, { useEffect, useState } from 'react';
import {
  Table, Button, Container, Alert, Pagination, Modal, Form,
  Badge, Row, Col, Card, Spinner, InputGroup
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaFilter, FaUser, FaUserShield, FaUniversity, FaIdBadge } from 'react-icons/fa';
import { authAPI, universityAPI } from '../../api/wbuoaApi';

const UserList = () => {
  // State management
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    emp_name: '',
    email: '',
    password: '',
    user_id: '',
    role: 'user',
    university: '',
    designation: '',
    mobile: '',
    office_bearer: 'member'
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedOfficeBearer, setSelectedOfficeBearer] = useState('all');
  const [selectedUniversity, setSelectedUniversity] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'emp_name', direction: 'asc' });

  const itemsPerPage = 10;

  // Form label style
  const formLabelStyle = {
    color: '#1a4a2f',
    fontWeight: '500'
  };

  // Role options
  const roleOptions = ['admin', 'user'];

  // Office bearer options
  const officeBearerOptions = ['member', 'secretary', 'president'];

  // Fetch users when component mounts
  useEffect(() => {
    fetchUsers();
    fetchUniversities();
  }, []);

  // Apply filters when users or filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, selectedRole, selectedOfficeBearer, selectedUniversity, currentPage, sortConfig]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await authAPI.listUsers();
      // API returns array directly or wrapped in data property
      const usersData = Array.isArray(response) ? response : (response.data || []);

      setUsers(usersData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users: ' + (err.message || 'Server error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch universities from API - FIXED: Using correct method name listUniversitiesAll
  const fetchUniversities = async () => {
    setIsLoadingUniversities(true);
    try {
      // Using the correct method name from wbuoaApi.js
      const response = await universityAPI.listUniversitiesAll();
      console.log('Universities API Response:', response);

      // The API returns an array of university objects directly
      const universitiesData = Array.isArray(response) ? response : [];

      // Extract university names and filter out empty/null values
      const universityNames = universitiesData
        .map(u => u.University)
        .filter(name => name && name.trim() !== '')
        .sort((a, b) => a.localeCompare(b));

      console.log('Extracted University Names:', universityNames);
      setUniversities(universityNames);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch universities:', err);
      // Don't show error to user, just log it and set empty array
      setUniversities([]);
    } finally {
      setIsLoadingUniversities(false);
    }
  };

  // Apply filters, sorting and pagination
  const applyFilters = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.emp_name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.user_id?.toLowerCase().includes(term) ||
        user.university?.toLowerCase().includes(term) ||
        user.designation?.toLowerCase().includes(term) ||
        user.mobile?.toLowerCase().includes(term)
      );
    }

    // Apply role filter
    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    // Apply office bearer filter
    if (selectedOfficeBearer !== 'all') {
      filtered = filtered.filter(user => user.office_bearer === selectedOfficeBearer);
    }

    // Apply university filter
    if (selectedUniversity !== 'all') {
      filtered = filtered.filter(user => user.university === selectedUniversity);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key) {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    // Calculate pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    setFilteredUsers(paginated);
    setTotalItems(filtered.length);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedRole('all');
    setSelectedOfficeBearer('all');
    setSelectedUniversity('all');
    setCurrentPage(1);
    setSortConfig({ key: 'emp_name', direction: 'asc' });
  };

  // Get unique universities from users
  const getUniqueUniversities = () => {
    return [...new Set(users.map(user => user.university))].filter(u => u).sort();
  };

  // Get stats
  const getStats = () => {
    const total = users.length;
    const admins = users.filter(user => user.role === 'admin').length;
    const regularUsers = users.filter(user => user.role === 'user').length;
    const secretaries = users.filter(user => user.office_bearer === 'secretary').length;
    const presidents = users.filter(user => user.office_bearer === 'president').length;
    const universities = [...new Set(users.map(user => user.university))].filter(u => u).length;

    return {
      total,
      admins,
      regularUsers,
      secretaries,
      presidents,
      universities
    };
  };

  const stats = getStats();
  const userUniversities = getUniqueUniversities();

  // Validate form function
  const validateForm = () => {
    const errors = {};
    if (!formData.emp_name.trim()) errors.emp_name = 'Employee name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.password && !currentUser) errors.password = 'Password is required';
    if (!formData.user_id.trim()) errors.user_id = 'User ID is required';
    if (!formData.office_bearer) errors.office_bearer = 'Office bearer is required';
    return errors;
  };

  // Handle create new user submission
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsCreating(true);
    try {
      await authAPI.register(formData);
      setShowCreateModal(false);
      setCurrentPage(1); // Reset to first page after creation
      await fetchUsers(); // Refresh the list
      setSuccess('User created successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to create user: ${err.response?.data?.message || err.message || 'Please try again'}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle user deletion
  const handleDelete = async (id) => {
    if (!id) {
      setError('Invalid user ID');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setDeletingId(id);
    try {
      await authAPI.deleteUser(id);
      setSuccess('User deleted successfully');

      // Refresh the list
      await fetchUsers();

      // Reset to first page if needed
      if (filteredUsers.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to delete user: ${err.message || 'Server error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle edit button click
  const handleEdit = (user) => {
    if (!user?.id) {
      setError('Invalid user data');
      return;
    }

    setCurrentUser(user);
    setFormData({
      emp_name: user.emp_name || '',
      email: user.email || '',
      password: '',
      user_id: user.user_id || '',
      role: user.role || 'user',
      university: user.university || '',
      designation: user.designation || '',
      mobile: user.mobile || '',
      office_bearer: user.office_bearer || 'member'
    });
    setShowEditModal(true);
  };

  // Handle create new user
  const handleCreate = () => {
    setCurrentUser(null);
    setFormData({
      emp_name: '',
      email: '',
      password: '',
      user_id: '',
      role: 'user',
      university: '',
      designation: '',
      mobile: '',
      office_bearer: 'member'
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  // Handle user update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentUser?.id) {
      setError('No user selected for update');
      return;
    }

    setIsUpdating(true);
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      await authAPI.updateUser(currentUser.id, updateData);
      setShowEditModal(false);
      await fetchUsers();
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update user: ${err.message || 'Please try again'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate pagination items
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  return (
    <Container fluid className="py-4">
      {/* Header Section */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">User Management</h2>
              <p className="text-muted mb-0">
                Manage system users, roles, and permissions.
              </p>
            </div>
            <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
              <FaPlus className="me-2" />
              Add User
            </Button>
          </div>

          {/* Search and Filter Section */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search users by name, email, ID, or university..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={selectedOfficeBearer}
                onChange={(e) => {
                  setSelectedOfficeBearer(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Positions</option>
                <option value="member">Member</option>
                <option value="secretary">Secretary</option>
                <option value="president">President</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={selectedUniversity}
                onChange={(e) => {
                  setSelectedUniversity(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Universities</option>
                {userUniversities.map((university, index) => (
                  <option key={index} value={university}>
                    {university}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={resetFilters}
                className="w-100"
                title="Reset Filters"
              >
                <FaFilter /> Reset
              </Button>
            </Col>
          </Row>

          {/* Stats Cards */}
          <Row className="mt-4">
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Total Users</h6>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                    <div className="text-primary">
                      <FaUser size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Admins</h6>
                      <h4 className="mb-0">{stats.admins}</h4>
                    </div>
                    <div className="text-success">
                      <FaUserShield size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Regular Users</h6>
                      <h4 className="mb-0">{stats.regularUsers}</h4>
                    </div>
                    <div className="text-info">
                      <FaIdBadge size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Secretaries</h6>
                      <h4 className="mb-0">{stats.secretaries}</h4>
                    </div>
                    <div className="text-warning">
                      <Badge bg="warning">Sec</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Presidents</h6>
                      <h4 className="mb-0">{stats.presidents}</h4>
                    </div>
                    <div className="text-danger">
                      <Badge bg="danger">Pres</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Universities</h6>
                      <h4 className="mb-0">{stats.universities}</h4>
                    </div>
                    <div className="text-info">
                      <FaUniversity size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading users...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>University</th>
                      <th>Office Bearer</th>
                      <th>Designation</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(user => (
                        <tr key={user.id}>
                          <td>
                            <Badge bg="light" text="dark" className="fw-normal">
                              {user.user_id}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-semibold">{user.emp_name}</div>
                            {user.mobile && <small className="text-muted d-block">{user.mobile}</small>}
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '180px' }} title={user.email}>
                              {user.email}
                            </div>
                          </td>
                          <td>
                            <Badge
                              bg={user.role === 'admin' ? 'success' : 'info'}
                              className="text-uppercase"
                            >
                              {user.role}
                            </Badge>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '150px' }} title={user.university}>
                              {user.university || '—'}
                            </div>
                          </td>
                          <td>
                            <Badge
                              bg={
                                user.office_bearer === 'president' ? 'danger' :
                                  user.office_bearer === 'secretary' ? 'warning' : 'secondary'
                              }
                              className="text-uppercase"
                            >
                              {user.office_bearer}
                            </Badge>
                          </td>
                          <td>{user.designation || '—'}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                onClick={() => handleEdit(user)}
                                size="sm"
                                disabled={deletingId !== null}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                onClick={() => handleDelete(user.id)}
                                size="sm"
                                disabled={deletingId !== null}
                                title="Delete"
                              >
                                {deletingId === user.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FaTrash />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <div className="py-4">
                            <FaSearch size={48} className="text-muted mb-3" />
                            <h5>No users found</h5>
                            <p className="text-muted">
                              {searchTerm || selectedRole !== 'all' || selectedOfficeBearer !== 'all' || selectedUniversity !== 'all'
                                ? 'Try changing your search or filters'
                                : 'Add your first user to get started'}
                            </p>
                            {searchTerm || selectedRole !== 'all' || selectedOfficeBearer !== 'all' || selectedUniversity !== 'all' ? (
                              <Button variant="outline-primary" onClick={resetFilters}>
                                Reset Filters
                              </Button>
                            ) : (
                              <Button variant="primary" onClick={handleCreate}>
                                <FaPlus className="me-2" /> Add User
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredUsers.length > 0 && totalPages > 1 && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} users
                    </div>
                    <Pagination className="mb-0">
                      {renderPagination()}
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdate}>
            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="emp_name"
                value={formData.emp_name}
                onChange={handleChange}
                required
                disabled={isUpdating}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>User ID</Form.Label>
              <Form.Control
                type="text"
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
                required
                disabled={isUpdating}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>New Password (leave blank to keep current)</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter new password"
                disabled={isUpdating}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Role</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                disabled={isUpdating}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Office Bearer</Form.Label>
              <Form.Select
                name="office_bearer"
                value={formData.office_bearer}
                onChange={handleChange}
                required
                disabled={isUpdating}
              >
                <option value="member">Member</option>
                <option value="secretary">Secretary</option>
                <option value="president">President</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>University</Form.Label>
              {isLoadingUniversities ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="text-muted">Loading universities...</span>
                </div>
              ) : (
                <Form.Select
                  name="university"
                  value={formData.university}
                  onChange={handleChange}
                  disabled={isUpdating}
                >
                  <option value="">Select University</option>
                  {universities.length > 0 ? (
                    universities.map((university, index) => (
                      <option key={index} value={university}>
                        {university}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No universities available</option>
                  )}
                </Form.Select>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Designation</Form.Label>
              <Form.Control
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                disabled={isUpdating}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Mobile</Form.Label>
              <Form.Control
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                disabled={isUpdating}
              />
            </Form.Group>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setShowEditModal(false)} className="me-2" disabled={isUpdating}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Updating...
                  </>
                ) : (
                  'Update User'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Employee Name*</Form.Label>
              <Form.Control
                type="text"
                name="emp_name"
                value={formData.emp_name}
                onChange={handleChange}
                isInvalid={!!formErrors.emp_name}
                required
                disabled={isCreating}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.emp_name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Email*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!formErrors.email}
                required
                disabled={isCreating}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Password*</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                isInvalid={!!formErrors.password}
                required
                disabled={isCreating}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>User ID*</Form.Label>
              <Form.Control
                type="text"
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
                isInvalid={!!formErrors.user_id}
                required
                disabled={isCreating}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.user_id}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Office Bearer*</Form.Label>
              <Form.Select
                name="office_bearer"
                value={formData.office_bearer}
                onChange={handleChange}
                isInvalid={!!formErrors.office_bearer}
                required
                disabled={isCreating}
              >
                <option value="president">President</option>
                <option value="vice president">Vice President</option>
                <option value="secretary">Secretary</option>
                <option value="joint secretary">Joint Secretary</option>
                <option value="treasurer">Treasurer</option>
                <option value="joint treasurer">Joint Treasurer</option>
                <option value="assistant secretary">Assistant Secretary</option>
                <option value="advisor">Advisor</option>
                <option value="member">Member</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.office_bearer}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Role</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={isCreating}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>University</Form.Label>
              {isLoadingUniversities ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="text-muted">Loading universities...</span>
                </div>
              ) : (
                <Form.Select
                  name="university"
                  value={formData.university}
                  onChange={handleChange}
                  disabled={isCreating}
                >
                  <option value="">Select University</option>
                  {universities.length > 0 ? (
                    universities.map((university, index) => (
                      <option key={index} value={university}>
                        {university}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No universities available</option>
                  )}
                </Form.Select>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Designation</Form.Label>
              <Form.Control
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                disabled={isCreating}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={formLabelStyle}>Mobile</Form.Label>
              <Form.Control
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                disabled={isCreating}
              />
            </Form.Group>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="me-2" disabled={isCreating}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default UserList;